import { Icon as LegacyIcon } from "@ant-design/compatible";
import { Link } from "react-router-dom";
import { MenuProps } from "antd";
import { MenuItem } from "../../menu/menuTypes";

type AntMenuItem = Required<MenuProps>["items"][number];

export function buildMenuItems(
  menu: MenuItem[],
  collapsed: boolean,
  extraGroup?: AntMenuItem
): AntMenuItem[] {
  const grouped = menu.reduce<Record<string, MenuItem[]>>((acc, item) => {
    const section = item.section || "Otros";
    acc[section] = acc[section] || [];
    acc[section].push(item);
    return acc;
  }, {});

  return Object.entries(grouped).flatMap(([section, sectionItems]) => {
    const groupChildren: AntMenuItem[] = [];
    const visibleItems = sectionItems.filter((item) => item.show !== false);

    groupChildren.push(
      ...visibleItems.map((item) =>
        item.submenu
          ? {
              key: item.path,
              icon: item.icon ? (
                <LegacyIcon type={item.icon} className="customized" />
              ) : undefined,
              label: collapsed ? null : item.title,
              children: item.items?.map((subItem) => ({
                key: subItem.path,
                label: (
                  <Link to={subItem.path} title={subItem.title}>
                    {subItem.title}
                  </Link>
                ),
              })),
            }
          : {
              key: item.path,
              icon: item.icon ? (
                <LegacyIcon type={item.icon} className="customized" />
              ) : undefined,
              label: (
                <Link to={item.path} title={item.title}>
                  {item.title}
                </Link>
              ),
            }
      )
    );

    // Si es el segundo grupo, metemos extraGroup al inicio
    if (section === "Instalación" && extraGroup) {
      groupChildren.unshift(extraGroup);
    }

    if (!collapsed && section !== "Otros") {
      return [
        {
          key: `section-${section}`,
          label: <span className="menuSectionTitle">{section}</span>,
          type: "group",
          children: groupChildren,
        },
      ];
    }

    return groupChildren;
  });
}

export function findMenuItemByPath(
  menu: MenuItem[],
  pathname: string
): MenuItem | undefined {
  const findRecursive = (items: MenuItem[]): MenuItem | undefined => {
    for (const item of items) {
      if (item.path === pathname && !item.submenu) return item;
      if (item.items) {
        const found = findRecursive(item.items);
        if (found) return found;
      }
    }
    return undefined;
  };

  // Buscar coincidencia exacta
  const exactMatch = findRecursive(menu);
  if (exactMatch) return exactMatch;

  const flattenItems = (items: MenuItem[]): MenuItem[] =>
    items.reduce<MenuItem[]>((acc, item) => {
      acc.push(item);
      if (item.items) {
        acc.push(...flattenItems(item.items));
      }
      return acc;
    }, []);

  const flatMenu = flattenItems(menu);

  flatMenu.sort((a, b) => b.path.length - a.path.length);

  // Buscar la primera ruta que sea prefijo de pathname
  return flatMenu.find((item) => pathname.startsWith(item.path));
}

export function findMenuTitleByPath(
  menu: MenuItem[],
  pathname: string
): string {
  const menuItem = findMenuItemByPath(menu, pathname);
  return menuItem?.title ?? "";
}
